import { CreateUserDialog } from "./-components/create-user-dialog"
import { createFileRoute, useNavigate } from "@tanstack/react-router"
import { z } from "zod"
import { useQuery } from "convex/react"
import { type ColumnDef } from "@tanstack/react-table"
import type { Doc } from "@convex/_generated/dataModel"
import { api } from "@convex/_generated/api"
import { Badge } from "@/components/ui/badge"
import { Card, CardContent } from "@/components/ui/card"
import { DataTable } from "@/components/ui/data-table"
import { DataTableColumnHeader } from "@/components/ui/data-table-column-header"
import { Skeleton } from "@/components/ui/skeleton"
import { getCountryFlag } from "@/lib/country-utils"
import { Users, Crown, Medal, Award } from "lucide-react"
import { formatDateInLocalTimezone } from "@/lib/timezone-utils"

// Extended user type with tier info
type UserWithTier = Doc<"users"> & { currentTier: string | null }

// Tier badge styling
const TIER_VARIANTS: Record<
  string,
  { variant: "default" | "secondary" | "outline"; className: string }
> = {
  gold: { variant: "default", className: "bg-yellow-500 hover:bg-yellow-500" },
  silver: { variant: "default", className: "bg-slate-400 hover:bg-slate-400" },
  bronze: {
    variant: "default",
    className: "bg-orange-600 hover:bg-orange-600",
  },
  "first-timer": {
    variant: "default",
    className: "bg-purple-500 hover:bg-purple-500",
  },
}

// Search params schema
const usersSearchSchema = z.object({
  search: z.string().catch(""),
})

export const Route = createFileRoute("/(admin)/admin/users/")({
  validateSearch: (search) => usersSearchSchema.parse(search),
  component: RouteComponent,
})

function RouteComponent() {
  const navigate = useNavigate()
  const { search } = Route.useSearch()
  const users = useQuery(api.users.handlers.listAllUsers, {
    search: search || undefined,
  })
  const subscriptionCounts = useQuery(
    api.subscriptions.handlers.getActiveSubscriptionCounts
  )

  // Define columns with navigation
  const columns: ColumnDef<UserWithTier>[] = [
    {
      accessorKey: "name",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Name" />
      ),
      cell: ({ row }) => (
        <span className="font-medium">{row.original.name}</span>
      ),
    },
    {
      accessorKey: "email",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Email" />
      ),
      cell: ({ row }) => row.original.email ?? "N/A",
    },
    {
      accessorKey: "currentTier",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Tier" />
      ),
      cell: ({ row }) => {
        const tier = row.original.currentTier
        if (!tier) {
          return <span className="text-muted-foreground">—</span>
        }
        const style = TIER_VARIANTS[tier] ?? {
          variant: "outline" as const,
          className: "",
        }
        return (
          <Badge variant={style.variant} className={style.className}>
            {tier.charAt(0).toUpperCase() + tier.slice(1)}
          </Badge>
        )
      },
    },
    {
      accessorKey: "country",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Country" />
      ),
      cell: ({ row }) => {
        const country = row.original.country
        if (!country) {
          return <span className="text-muted-foreground">—</span>
        }
        const flag = getCountryFlag(country)
        return (
          <span className="flex items-center gap-1.5">
            <span>{flag}</span>
            <span>{country}</span>
          </span>
        )
      },
    },
    {
      accessorKey: "tokenIdentifier",
      id: "status",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Status" />
      ),
      cell: ({ row }) => (
        <Badge variant={row.original.tokenIdentifier ? "default" : "outline"}>
          {row.original.tokenIdentifier ? "Active" : "Pre-Auth"}
        </Badge>
      ),
    },
    {
      accessorKey: "_creationTime",
      id: "joinDate",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Join Date" />
      ),
      cell: ({ row }) =>
        formatDateInLocalTimezone(new Date(row.original._creationTime).toISOString()),
    },
  ]

  if (users === undefined) {
    return <div className="p-4">Loading...</div>
  }

  return (
    <div className="flex flex-1 flex-col gap-4 p-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Users</h1>
          <p className="text-sm text-muted-foreground">
            Manage user accounts, subscriptions, and payments
          </p>
        </div>
        <CreateUserDialog />
      </div>

      {/* Subscription Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-primary/10">
                <Users className="h-5 w-5 text-primary" />
              </div>
              <div>
                {subscriptionCounts ? (
                  <p className="text-2xl font-bold">
                    {subscriptionCounts.total}
                  </p>
                ) : (
                  <Skeleton className="h-8 w-12" />
                )}
                <p className="text-xs text-muted-foreground">
                  Total Subscribed
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-yellow-500/10">
                <Crown className="h-5 w-5 text-yellow-500" />
              </div>
              <div>
                {subscriptionCounts ? (
                  <p className="text-2xl font-bold">
                    {subscriptionCounts.gold}
                  </p>
                ) : (
                  <Skeleton className="h-8 w-12" />
                )}
                <p className="text-xs text-muted-foreground">Gold</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-slate-400/10">
                <Medal className="h-5 w-5 text-slate-400" />
              </div>
              <div>
                {subscriptionCounts ? (
                  <p className="text-2xl font-bold">
                    {subscriptionCounts.silver}
                  </p>
                ) : (
                  <Skeleton className="h-8 w-12" />
                )}
                <p className="text-xs text-muted-foreground">Silver</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-orange-600/10">
                <Award className="h-5 w-5 text-orange-600" />
              </div>
              <div>
                {subscriptionCounts ? (
                  <p className="text-2xl font-bold">
                    {subscriptionCounts.bronze}
                  </p>
                ) : (
                  <Skeleton className="h-8 w-12" />
                )}
                <p className="text-xs text-muted-foreground">Bronze</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
      {subscriptionCounts?.quarterName && (
        <p className="text-xs text-muted-foreground -mt-2">
          Showing active subscriptions for {subscriptionCounts.quarterName}
        </p>
      )}

      <DataTable
        columns={columns}
        data={users}
        filterColumn="name"
        filterPlaceholder="Search by name..."
        onRowClick={(user) => {
          navigate({
            to: "/admin/users/$userId",
            params: { userId: user._id },
          }).catch(console.error)
        }}
      />
    </div>
  )
}
